import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { canAccessSecrets, roleSettled } from '../lib/roles';
import { useMe } from '../lib/session';
import { ConnectionTokensPanel } from './ConnectionTokensPanel';
import { Panel } from './Panel';

// Owner gate over the account's vault panel (#1418). All three `/v1/secrets`
// routes are owner-only now — the READ moved too, because the vault's NAMES are
// the disclosure — so a viewer/member/admin gets neither the list nor the Add
// control. The gate wraps rather than hides-within: `<ConnectionTokensPanel/>`
// calls `useSecrets` on mount, so keeping it UNMOUNTED is what stops the SPA
// firing a `GET /v1/secrets` that can only ever 403.
//
// Shut until `/v1/me` has SETTLED for this tenant (`roleSettled` — not
// `!isLoading`, which lets through both the org-switch placeholder carrying the
// previous org's role and a failed request), and `denied` is not `!allowed`:
// unsettled means unknown, not "no", and an owners-only line that flips away a
// moment later reads as a bug.

export function ConnectionTokens(props: { readonly token: string | null }): JSX.Element {
  const me = useMe(props.token);
  const allowed = (): boolean => roleSettled(me) && canAccessSecrets(me.data?.role);
  const denied = (): boolean => roleSettled(me) && !canAccessSecrets(me.data?.role);

  return (
    <Show
      when={allowed()}
      fallback={
        // The panel stays, saying why it's empty: it silently vanishing would
        // read as a removed feature to an admin who used it yesterday.
        <Show when={denied()}>
          <Panel title={t('connections.title')} class="connections">
            <p class="list-empty" role="status">
              {t('connections.owner_only')}
            </p>
          </Panel>
        </Show>
      }
    >
      <ConnectionTokensPanel token={props.token} />
    </Show>
  );
}
